import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  BookOpen, 
  CheckCircle, 
  Target,
  PlayCircle,
  Lock,
  Award,
  ArrowLeft,
  ListChecks,
  Lightbulb
} from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { moduleContent } from '../data/moduleContent';
import { curriculum, quizQuestions } from '../data/mock';
import Quiz from './Quiz';
import { toast } from 'sonner';
import { saveLessonProgress, getModuleLessonProgress } from '../services/api';
import VideoPlayer from './VideoPlayer';
import MiniQuiz, { lessonQuizzes } from './MiniQuiz';

const ModulePage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [showQuiz, setShowQuiz] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [userId, setUserId] = useState(null);
  
  const moduleNum = parseInt(moduleId);
  const module = moduleContent[moduleNum];
  const curriculumData = curriculum.find(c => c.id === moduleNum);

  useEffect(() => {
    // Check enrollment status
    const leadId = localStorage.getItem('oshaLeadId');
    setIsEnrolled(!!leadId);
    setUserId(leadId);
    
    // Load saved progress from backend if logged in, otherwise localStorage
    const loadProgress = async () => {
      if (leadId) {
        try {
          const backendProgress = await getModuleLessonProgress(leadId, moduleNum);
          if (backendProgress && backendProgress.length > 0) {
            setCompletedLessons(backendProgress);
            // Also update localStorage as cache
            localStorage.setItem(`module_${moduleNum}_progress`, JSON.stringify(backendProgress));
          } else {
            // Fallback to localStorage
            const savedProgress = localStorage.getItem(`module_${moduleNum}_progress`);
            if (savedProgress) {
              setCompletedLessons(JSON.parse(savedProgress));
            }
          }
        } catch (error) {
          console.error('Failed to load progress from backend:', error);
          // Fallback to localStorage
          const savedProgress = localStorage.getItem(`module_${moduleNum}_progress`);
          if (savedProgress) {
            setCompletedLessons(JSON.parse(savedProgress));
          }
        }
      } else {
        // Not logged in, use localStorage
        const savedProgress = localStorage.getItem(`module_${moduleNum}_progress`);
        if (savedProgress) {
          setCompletedLessons(JSON.parse(savedProgress));
        }
      }
    };
    
    loadProgress();
    
    // Scroll to top on module change
    window.scrollTo(0, 0);
  }, [moduleNum]);

  if (!module) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Module Not Found</h1>
          <Button onClick={() => navigate('/')}>Return to Course</Button>
        </div>
      </div>
    );
  }

  const lessons = module.lessons || [];
  const hasLessons = lessons.length > 0;
  const progress = hasLessons ? (completedLessons.length / lessons.length) * 100 : 0;
  const allLessonsComplete = hasLessons && completedLessons.length === lessons.length;

  const markLessonComplete = async (lessonId) => {
    if (!completedLessons.includes(lessonId)) {
      const newCompleted = [...completedLessons, lessonId];
      setCompletedLessons(newCompleted);
      localStorage.setItem(`module_${moduleNum}_progress`, JSON.stringify(newCompleted));
      
      // Sync to backend if logged in
      if (userId) {
        try {
          await saveLessonProgress(userId, moduleNum, lessonId);
        } catch (error) {
          console.error('Failed to save progress to backend:', error);
        }
      }
      
      toast.success('Lesson completed!');
    }
  };

  const goToNextLesson = () => {
    if (currentLesson < lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  };

  const goToPrevLesson = () => {
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  };

  const currentLessonData = lessons[currentLesson];

  // Module-specific SEO schema
  const moduleSchema = {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    "name": `Module ${moduleNum}: ${module.title}`,
    "description": module.description,
    "learningResourceType": "Online Course Module",
    "educationalLevel": "Beginner",
    "timeRequired": module.duration,
    "isPartOf": {
      "@type": "Course",
      "name": "OSHA Safety Training Certification Course"
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Helmet */}
      <Helmet>
        <title>{`${module.title} | OSHA Training Module ${moduleNum} | WSafe Training™`}</title>
        <meta name="description" content={module.description} />
        <meta property="og:title" content={`${module.title} - OSHA Training Module ${moduleNum}`} />
        <meta property="og:description" content={module.description} />
        <meta property="og:image" content={module.heroImage} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://wsafetraining.com/module/${moduleNum}`} />
        <script type="application/ld+json">
          {JSON.stringify(moduleSchema)}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/#curriculum" 
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Course</span>
              </Link>
              <div className="h-6 w-px bg-slate-300 hidden sm:block" />
              <span className="text-sm text-slate-500 hidden sm:inline">Module {moduleNum} of 8</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                <span>{module.duration}</span>
              </div>
              {hasLessons && (
                <div className="flex items-center gap-2">
                  <Progress value={progress} className="w-24 h-2" />
                  <span className="text-sm font-medium text-slate-600">{Math.round(progress)}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div 
        className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white py-16"
        style={{
          backgroundImage: module.heroImage ? `linear-gradient(to bottom right, rgba(15, 23, 42, 0.9), rgba(6, 78, 59, 0.85)), url(${module.heroImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-emerald-400 mb-4">
              <BookOpen className="w-5 h-5" />
              <span className="text-sm font-medium">Module {moduleNum}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{module.title}</h1>
            <p className="text-lg text-slate-300 mb-6">{module.subtitle}</p>
            <p className="text-slate-400">{module.description}</p>
            
            <div className="flex flex-wrap gap-4 mt-8">
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <Clock className="w-5 h-5 text-emerald-400" />
                <span>{module.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <BookOpen className="w-5 h-5 text-emerald-400" />
                <span>{lessons.length || curriculumData?.topicCount || 6} Lessons</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <Target className="w-5 h-5 text-emerald-400" />
                <span>{module.objectives?.length || 5} Objectives</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar - Course Navigation */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-emerald-600" />
                Module Contents
              </h3>
              
              {/* Learning Objectives */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Learning Objectives</h4>
                <ul className="space-y-2">
                  {module.objectives?.slice(0, 4).map((obj, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <Target className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Lesson List */}
              {hasLessons && (
                <div className="border-t border-slate-100 pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Lessons</h4>
                  <ul className="space-y-2">
                    {lessons.map((lesson, idx) => (
                      <li key={lesson.id}>
                        <button
                          onClick={() => setCurrentLesson(idx)}
                          className={`w-full text-left p-3 rounded-lg transition-all flex items-center gap-3 ${
                            currentLesson === idx
                              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                              : 'hover:bg-slate-50 text-slate-600'
                          }`}
                        >
                          {completedLessons.includes(lesson.id) ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          ) : (
                            <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${
                              currentLesson === idx ? 'border-emerald-500' : 'border-slate-300'
                            }`} />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{lesson.title}</p>
                            <p className="text-xs text-slate-500">{lesson.duration}</p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Quiz Button */}
              <div className="border-t border-slate-100 pt-4 mt-4">
                <Button
                  onClick={() => setShowQuiz(true)}
                  disabled={!isEnrolled}
                  className={`w-full ${allLessonsComplete ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-slate-200 text-slate-500'}`}
                >
                  {!isEnrolled ? (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Enroll to Access Quiz
                    </>
                  ) : allLessonsComplete ? (
                    <>
                      <Award className="w-4 h-4 mr-2" />
                      Take Module Quiz
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Complete Lessons First
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            {showQuiz ? (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900">Module {moduleNum} Quiz</h2>
                  <Button variant="outline" size="sm" onClick={() => setShowQuiz(false)}>
                    Back to Content
                  </Button>
                </div>
                <Quiz moduleId={moduleNum} isEnrolled={true} onModuleChange={() => {}} />
              </div>
            ) : hasLessons && currentLessonData ? (
              <>
                {/* Lesson Content */}
                <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 mb-6">
                  <div className="flex items-center gap-2 text-emerald-600 mb-2">
                    <PlayCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Lesson {currentLesson + 1} of {lessons.length}</span>
                  </div>
                  
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">{currentLessonData.title}</h2>
                  <p className="text-slate-500 mb-6 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {currentLessonData.duration}
                  </p>

                  {/* Video Player */}
                  <VideoPlayer 
                    moduleId={moduleNum}
                    lessonId={currentLessonData.id}
                    title={currentLessonData.title}
                    duration={currentLessonData.duration}
                    poster={module.heroImage}
                  />

                  {/* Lesson Content HTML */}
                  <div 
                    className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-li:text-slate-600 prose-table:text-sm"
                    dangerouslySetInnerHTML={{ __html: currentLessonData.content }}
                  />

                  {/* Key Points */}
                  {currentLessonData.keyPoints && (
                    <div className="mt-8 p-6 bg-emerald-50 rounded-xl border border-emerald-100">
                      <h4 className="font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5" />
                        Key Takeaways
                      </h4>
                      <ul className="space-y-2">
                        {currentLessonData.keyPoints.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-emerald-700">
                            <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Mini Quiz - Knowledge Check */}
                  {lessonQuizzes[moduleNum]?.[currentLessonData.id] && (
                    <MiniQuiz 
                      {...lessonQuizzes[moduleNum][currentLessonData.id]}
                      onComplete={(correct) => {
                        if (correct) {
                          // Optionally auto-mark lesson complete on correct answer
                        }
                      }}
                    />
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={goToPrevLesson}
                    disabled={currentLesson === 0}
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>

                  <Button
                    onClick={() => {
                      markLessonComplete(currentLessonData.id);
                      if (currentLesson < lessons.length - 1) {
                        goToNextLesson();
                      }
                    }}
                    className="bg-emerald-500 hover:bg-emerald-600 flex items-center gap-2"
                  >
                    {completedLessons.includes(currentLessonData.id) ? (
                      currentLesson < lessons.length - 1 ? (
                        <>
                          Next Lesson
                          <ChevronRight className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Complete
                        </>
                      )
                    ) : (
                      <>
                        Mark Complete & Continue
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              /* Module Overview when no lessons */
              <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Module Overview</h2>
                
                {/* Topics from curriculum */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Topics Covered</h3>
                  <ul className="grid md:grid-cols-2 gap-3">
                    {curriculumData?.topics.map((topic, idx) => (
                      <li key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Summary */}
                {module.summary && (
                  <div className="mb-8 p-6 bg-emerald-50 rounded-xl border border-emerald-100">
                    <h3 className="text-lg font-semibold text-emerald-800 mb-3">What You'll Learn</h3>
                    <p className="text-emerald-700">{module.summary}</p>
                  </div>
                )}

                {/* Next Steps */}
                {module.nextSteps && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Next Steps</h3>
                    <ul className="space-y-3">
                      {module.nextSteps.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                            {idx + 1}
                          </div>
                          <span className="text-slate-700">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Take Quiz Button */}
                <div className="flex justify-center">
                  <Button
                    onClick={() => setShowQuiz(true)}
                    disabled={!isEnrolled}
                    size="lg"
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    {!isEnrolled ? (
                      <>
                        <Lock className="w-5 h-5 mr-2" />
                        Enroll to Take Quiz
                      </>
                    ) : (
                      <>
                        <Award className="w-5 h-5 mr-2" />
                        Take Module {moduleNum} Quiz
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Module Navigation */}
            <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-8">
              {moduleNum > 1 ? (
                <Link
                  to={`/module/${moduleNum - 1}`}
                  className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <div className="text-left">
                    <p className="text-xs text-slate-500">Previous Module</p>
                    <p className="font-medium">Module {moduleNum - 1}</p>
                  </div>
                </Link>
              ) : (
                <div />
              )}
              
              {moduleNum < 8 ? (
                <Link
                  to={`/module/${moduleNum + 1}`}
                  className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
                >
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Next Module</p>
                    <p className="font-medium">Module {moduleNum + 1}</p>
                  </div>
                  <ChevronRight className="w-5 h-5" />
                </Link>
              ) : (
                <Link
                  to="/#certificate"
                  className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors"
                >
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Complete Course</p>
                    <p className="font-medium">Get Certificate</p>
                  </div>
                  <Award className="w-5 h-5" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulePage;
